import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert
} from "@mui/material";
import { usePipelineStages } from "../../hooks/usePipelineStages";
import PipelineStageTable from "../../components/PipelineStageTable";
import AddPipelineStageForm from "../../components/AddPipelineStageForm";
import EmptyStateWithAI from "../../components/EmptyStateWithAI";
import { usePageContext } from "../../context/PageContext";
import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { fetchData } from "../../api/client";
import { buildSetupAiDadosEtapaFromWorkspace } from "../../utils/buildSetupAiDadosEtapa";

const EXPLICACAO_PIPELINE =
  "As etapas do pipeline representam a jornada de vendas ou de oportunidades do seu negócio — da prospecção ao fechamento. Definir etapas claras (ex.: Prospecção, Qualificação, Proposta, Negociação, Ganho/Perdido) ajuda a organizar o funil, medir conversão e aplicar automações em cada fase.";

const PipelineStages = () => {
  const { byKey } = useWorkspaceConfig();
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedStage, setSelectedStage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, error, refetch, updateMutation, deleteMutation, isLoading } =
    usePipelineStages(currentPage, pageSize, search);

  const stages = data?.stages || [];
  const isEmpty = !isLoading && stages.length === 0;
  const [loadingSugestao, setLoadingSugestao] = useState(false);

  const handleCriarComSugestoesIA = async () => {
    setLoadingSugestao(true);
    setErrorMessage(null);
    try {
      const response = await fetchData("/setup", "POST", {
        etapaAtual: 4,
        dadosEtapa: buildSetupAiDadosEtapaFromWorkspace(byKey),
      });
      const raw = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      const suggested = Array.isArray(raw)
        ? raw.map((item, i) => ({
            title: item?.title || item?.nome || `Etapa ${i + 1}`,
            description: item?.description || "",
          }))
        : [];
      if (suggested.length === 0) {
        setErrorMessage("Nenhuma sugestão retornada. Tente novamente.");
        return;
      }
      for (let i = 0; i < suggested.length; i++) {
        await updateMutation.mutateAsync({
          nome: suggested[i].title,
          ordem: i + 1,
          probabilidade: i === suggested.length - 1 ? 100 : Math.round((100 * (i + 1)) / suggested.length),
          cor: "#607D8B",
          tipo: i === suggested.length - 1 ? "ganho" : "normal",
          sla_dias: null,
          status: 1,
        });
      }
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao criar etapas com IA.");
    } finally {
      setLoadingSugestao(false);
    }
  };

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar etapas. Por favor, tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  // Handlers individuais
  const handleEditStage = (stage) => {
    setSelectedStage(stage);
    setErrorMessage(null);
  };
  const handleDeleteStage = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };
  const confirmDeleteStage = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => { setConfirmDelete(null); refetch(); },
      onError: (err) => setErrorMessage(err?.response?.data?.message || "Erro ao excluir a etapa."),
      onSettled: () => setIsProcessing(false),
    });
  };

  // Toggle status
  const toggleStatus = (stage) => {
    setIsProcessing(true);
    updateMutation.mutate(
      { id: stage.id, status: stage.status ? 0 : 1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao alterar status."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  // Ações em massa
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao ativar etapas.");
    } finally { setIsProcessing(false); }
  };
  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao desativar etapas.");
    } finally { setIsProcessing(false); }
  };
  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao excluir etapas.");
    } finally { setIsProcessing(false); }
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => { setSearch(term); refetch(); };
  const handleCloseForm = () => { setSelectedStage(null); setErrorMessage(null); };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>Etapas do Pipeline</Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedStage({})}
        >
          Adicionar Etapa
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedStage ? (
        <AddPipelineStageForm
          stageData={selectedStage}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(updatedStage) => {
            setIsProcessing(true);
            updateMutation.mutate(updatedStage, {
              onSuccess: () => { refetch(); handleCloseForm(); },
              onError: (err) =>
                setErrorMessage(err?.response?.data?.message || "Erro ao salvar a etapa."),
              onSettled: () => setIsProcessing(false),
            });
          }}
        />
      ) : isEmpty ? (
        <EmptyStateWithAI
          title="Nenhuma etapa do pipeline cadastrada"
          description={EXPLICACAO_PIPELINE}
          primaryLabel="Criar etapas com sugestões de IA"
          secondaryLabel="Adicionar etapa manualmente"
          onPrimaryClick={handleCriarComSugestoesIA}
          onSecondaryClick={() => setSelectedStage({})}
          primaryLoading={loadingSugestao}
        />
      ) : (
        <PipelineStageTable
          stages={stages}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditStage={handleEditStage}
          onDeleteStage={handleDeleteStage}
          onToggleStatus={toggleStatus}
          onBulkActivate={bulkActivate}
          onBulkDeactivate={bulkDeactivate}
          onBulkDelete={bulkDelete}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? "Processando exclusão..." : "Deseja excluir esta etapa?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteStage}>Excluir</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PipelineStages;
